import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";

import { invalidateEntityHistory } from "@/api/admin/auditLog";
import { brandsApi } from "@/api/admin/brands/brandApi";
import {
  ensureMenuExists,
  getMenuCacheEntry,
  menuQueryKey,
  setMenuQueryData,
} from "@/api/admin/brands/menuQueryUtils";
import type { BrandMenuGet, BrandMenuPatch } from "@/api/admin/brands/types";
import { toastSuccess } from "@/components/molecules";
import type { BranchAvailability } from "@/components/units/DishDrawer";
import { rootStore } from "@/store/RootStore";

import {
  findCreatedCategoryInMenu,
  mapCategoryFormToCreatePost,
  mapCategoryFormToPatch,
  mapCategoryGetToForm,
  mapCategoryIdsToReorderItems,
  mapDishFormToCreatePost,
  mapDishFormToUpdatePatch,
  mapDishGetToForm,
  mapMenuGetToForm,
  mapParsedCategoriesToFullMenu,
  toUnavailableBranchIds,
} from "../menuMappers";
import type { CategoryType, DishFormType } from "../types";
import { type MenuActions, MODE_MENU_ACTION } from "./MenuActionsContext";

interface Options {
  onMenuUpdated?: (menu: BrandMenuGet) => void;
}

export const useMenuActionsProvider = (
  brandId: string | undefined,
  options: Options = {}
): MenuActions => {
  const menuStore = rootStore.menuStore;
  const queryClient = useQueryClient();
  const { onMenuUpdated } = options;

  const invalidateMenuQuery = useCallback(async () => {
    if (!brandId) return;
    await queryClient.invalidateQueries({
      queryKey: menuQueryKey(brandId),
    });
    await invalidateEntityHistory({ scope: brandId });
  }, [brandId, queryClient]);

  const applyMenuGet = useCallback(
    (menu: BrandMenuGet) => {
      const formMenu = {
        ...mapMenuGetToForm(menu),
        cuisine: menuStore.menu.cuisine ?? [],
      };
      menuStore.initMenu(formMenu);
      onMenuUpdated?.(menu);
      return formMenu;
    },
    [menuStore, onMenuUpdated]
  );

  const patchMenu = useCallback(
    async (payload: BrandMenuPatch) => {
      if (!brandId) return;
      await ensureMenuExists(brandId);
      const result = await brandsApi.patchMenu(brandId, payload);
      applyMenuGet(result);
      setMenuQueryData(brandId, result);
      return result;
    },
    [applyMenuGet, brandId]
  );

  const createCategory = useCallback(
    async (nameEn: string, nameAr?: string) => {
      if (!brandId) return;

      await ensureMenuExists(brandId);

      const order = menuStore.menu.categories?.length ?? 0;
      const payload = mapCategoryFormToCreatePost(nameEn, nameAr, order);
      const menu = await brandsApi.createMenuCategory(brandId, payload);

      const formMenu = applyMenuGet(menu);

      const createdCategory = findCreatedCategoryInMenu(menu, payload);

      toastSuccess("Category added successfully");

      return createdCategory
        ? mapCategoryGetToForm(createdCategory, menuStore.priceDecimals)
        : formMenu.categories?.[formMenu.categories.length - 1];
    },
    [applyMenuGet, brandId, menuStore]
  );

  const updateCategory = useCallback(
    async (categoryId: string, nameEn: string, nameAr?: string) => {
      const existing = menuStore.menu.categories?.find(
        c => c.id === categoryId
      );
      const order = existing?.order ?? 0;

      if (brandId) {
        const menu = await brandsApi.updateMenuCategory(
          brandId,
          categoryId,
          mapCategoryFormToPatch(nameEn, nameAr, order)
        );
        applyMenuGet(menu);
        toastSuccess("Category updated successfully");
        return;
      }

      menuStore.updateCategory(categoryId, nameEn, nameAr);
      toastSuccess("Category updated successfully");
    },
    [applyMenuGet, brandId, menuStore]
  );

  const deleteCategory = useCallback(
    async (categoryId: string) => {
      if (!brandId) return;
      await brandsApi.deleteMenuCategory(brandId, categoryId);
      menuStore.removeCategory(categoryId);
      toastSuccess("Category deleted successfully");
    },
    [brandId, menuStore]
  );

  const reorderCategories = useCallback(
    async (orderedCategoryIds: string[]) => {
      menuStore.updateCategoryOrder(orderedCategoryIds);
      if (!brandId) {
        toastSuccess("Categories reordered successfully");
        return;
      }

      await brandsApi.reorderMenuCategories(brandId, {
        items: mapCategoryIdsToReorderItems(orderedCategoryIds),
      });
      toastSuccess("Categories reordered successfully");
    },
    [brandId, menuStore]
  );

  const reorderDishes = useCallback(
    async (categoryId: string, orderedDishIds: string[]) => {
      menuStore.updateDishOrder(categoryId, orderedDishIds);
      if (!brandId) {
        toastSuccess("Dishes reordered successfully");
        return;
      }

      await brandsApi.reorderMenuDishes(brandId, categoryId, {
        items: mapCategoryIdsToReorderItems(orderedDishIds),
      });
      toastSuccess("Dishes reordered successfully");
    },
    [brandId, menuStore]
  );

  const saveDish = useCallback(
    async (
      categoryId: string,
      dish: DishFormType,
      branchAvailability: BranchAvailability[],
      mode: MODE_MENU_ACTION
    ) => {
      const unavailableBranchIds = toUnavailableBranchIds(branchAvailability);

      const category = menuStore.menu.categories?.find(
        c => c.id === categoryId
      );
      const displayOrder =
        mode === MODE_MENU_ACTION.CREATE
          ? (category?.dishes.length ?? 0)
          : Math.max(category?.dishes.findIndex(d => d.id === dish.id) ?? 0, 0);

      if (!brandId) {
        menuStore.addOrUpdateDishToCurrentCategory(
          dish,
          branchAvailability,
          categoryId
        );
        toastSuccess(
          mode === MODE_MENU_ACTION.CREATE
            ? "Dish added successfully"
            : "Dish updated successfully"
        );
        return dish;
      }

      if (mode === MODE_MENU_ACTION.CREATE) {
        const created = await brandsApi.createMenuDish(
          brandId,
          categoryId,
          mapDishFormToCreatePost(dish, displayOrder, unavailableBranchIds)
        );
        const mapped = mapDishGetToForm(created, menuStore.priceDecimals);
        menuStore.addOrUpdateDishToCurrentCategory(
          mapped,
          branchAvailability,
          categoryId
        );
        toastSuccess("Dish added successfully");
        return mapped;
      }

      if (!dish.id) {
        throw new Error("Dish id is required for update");
      }

      const updated = await brandsApi.updateMenuDish(
        brandId,
        dish.id,
        mapDishFormToUpdatePatch(
          dish,
          displayOrder,
          unavailableBranchIds,
          categoryId
        )
      );
      const mapped = mapDishGetToForm(updated, menuStore.priceDecimals);
      menuStore.addOrUpdateDishToCurrentCategory(
        mapped,
        branchAvailability,
        categoryId
      );
      toastSuccess("Dish updated successfully");
      return mapped;
    },
    [brandId, menuStore]
  );

  const persistParsedMenu = useCallback(
    async (parsedCategories: CategoryType[]) => {
      if (!brandId) return;

      const { menu: cachedMenu } = getMenuCacheEntry(brandId);
      await ensureMenuExists(brandId);
      const payload = mapParsedCategoriesToFullMenu(
        cachedMenu ?? { id: "", categories: [] },
        parsedCategories
      );

      const finalMenu = await brandsApi.postFullMenu(brandId, payload);
      const formMenu = applyMenuGet(finalMenu);
      setMenuQueryData(brandId, finalMenu);

      await invalidateMenuQuery();
      toastSuccess("Menu extracted successfully");

      return formMenu;
    },
    [applyMenuGet, brandId, invalidateMenuQuery]
  );

  const deleteDish = useCallback(
    async (categoryId: string, dishId: string) => {
      if (brandId) {
        await brandsApi.deleteMenuDish(brandId, dishId);
      }
      menuStore.removeDishFromCategory(dishId, categoryId);
      toastSuccess("Dish deleted successfully");
    },
    [brandId, menuStore]
  );

  const fetchDish = useCallback(
    async (dishId: string) => {
      if (!brandId) return;
      const dish = await brandsApi.getMenuDish(brandId, dishId);
      return mapDishGetToForm(dish, menuStore.priceDecimals);
    },
    [brandId, menuStore.priceDecimals]
  );

  const updateDishActive = useCallback(
    async (dishId: string, active: boolean) => {
      menuStore.updateDishActive(dishId, active);

      if (!brandId) return;

      const updated = await brandsApi.updateMenuDish(brandId, dishId, {
        active,
      });
      const mapped = mapDishGetToForm(updated, menuStore.priceDecimals);
      const categoryId = (menuStore.menu.categories ?? []).find(c =>
        c.dishes.some(d => d.id === dishId)
      )?.id;

      if (categoryId) {
        menuStore.addOrUpdateDishToCurrentCategory(
          mapped,
          undefined,
          categoryId
        );
      }

      await invalidateMenuQuery();
    },
    [brandId, invalidateMenuQuery, menuStore]
  );

  return useMemo(
    () => ({
      isEnabled: !!brandId,
      patchMenu,
      createCategory,
      updateCategory,
      deleteCategory,
      reorderCategories,
      reorderDishes,
      saveDish,
      persistParsedMenu,
      deleteDish,
      fetchDish,
      updateDishActive,
      invalidateMenuQuery,
    }),
    [
      brandId,
      patchMenu,
      createCategory,
      updateCategory,
      deleteCategory,
      reorderCategories,
      reorderDishes,
      saveDish,
      persistParsedMenu,
      deleteDish,
      fetchDish,
      updateDishActive,
      invalidateMenuQuery,
    ]
  );
};
